import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { z } from "zod";

import { DocumentViewerFiltersSchema } from "components/document_details/FilterForm";
import { DEFAULT_DOCUMENT_FILTERS, DocumentFilters } from "components/documents/utils";
import { useURLState } from "lib/hooks/useURLState";

// Lazy so importing the provider (e.g. in the global app shell or the test
// harness) doesn't eagerly pull in the heavy PDF/Apryse drawer chain; it loads
// only once a document is actually opened.
const DocumentDrawer = lazy(() => import("components/document_details/DocumentDrawer"));

// The open document is mirrored in this URL param so browser back/forward and deep
// links restore the slide-out. Namespaced separately from the notice preview
// (`noticePreview`, see RecordViewer) so the two drawers don't collide.
const PREVIEW_PARAM = "documentPreview";
const previewIdSchema = z.string().min(1).optional();

type ViewDocumentOptions = {
  // Ordered ids of the surrounding list (e.g. the current page of a table) so the
  // drawer's prev/next can walk them. Absent on a back/deep-link restore, which
  // leaves prev/next disabled.
  siblingIds?: string[];
  // List search filters, used by the viewer for initial keyword highlighting.
  filters?: DocumentFilters;
};

type DocumentViewer = {
  viewDocument: (documentId: string, options?: ViewDocumentOptions) => void;
  closeDocument: () => void;
  documentId: string | undefined;
};

const DocumentViewerContext = React.createContext<DocumentViewer | undefined>(undefined);

export function DocumentViewerProvider({ children }: { children: React.ReactNode }): JSX.Element {
  // The `documentPreview` URL param is the source of truth for which document is
  // open, so navigating away and pressing back restores the slide-out. `siblingIds`
  // and `filters` can't live in the URL, so they stay in local state — set when a
  // document is opened in-session, and absent on a back/deep-link restore.
  const [previewId, setPreviewId] = useURLState<typeof previewIdSchema>(
    PREVIEW_PARAM,
    undefined,
    previewIdSchema,
  );

  // The in-drawer keyword search lives in its own URL param; reset it when opening a
  // different document so highlights don't leak across documents.
  const [, setDocViewerFilters] = useURLState(
    "docViewerFilters",
    { keywords: [] },
    DocumentViewerFiltersSchema,
  );

  // Local mirror of the open state, kept so the drawer can play its close animation
  // before the content unmounts (`documentId` clears after the delay; `previewId`
  // clears immediately).
  const [isOpen, setIsOpen] = useState(!!previewId);
  const [documentId, setDocumentId] = useState<string | undefined>(previewId);
  const [siblingIds, setSiblingIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<DocumentFilters>();

  useEffect(() => {
    if (previewId) {
      setDocumentId(previewId);
      setIsOpen(true);
      return;
    }
    setIsOpen(false);
    const animationWait = setTimeout(() => {
      setDocumentId(undefined);
      setSiblingIds([]);
      setFilters(undefined);
    }, 300);
    return () => clearTimeout(animationWait);
  }, [previewId]);

  const index = previewId ? siblingIds.indexOf(previewId) : -1;
  const canGoToPreviousDocument = index > 0;
  const canGoToNextDocument = index !== -1 && index < siblingIds.length - 1;

  // prev/next keep the registered siblingIds and filters and just move the URL param
  // to the adjacent document, resetting the in-drawer keyword search like an open.
  const goToSibling = useCallback(
    (targetIndex: number) => {
      const id = siblingIds[targetIndex];
      if (!id) return;
      setDocViewerFilters({ keywords: [] });
      setPreviewId(id);
    },
    [siblingIds, setDocViewerFilters, setPreviewId],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) setPreviewId(undefined);
    },
    [setPreviewId],
  );

  const context = useMemo(
    () => ({
      viewDocument: (id: string, options?: ViewDocumentOptions) => {
        if (id !== previewId) {
          setDocViewerFilters({ keywords: [] });
        }
        setSiblingIds(options?.siblingIds ?? []);
        setFilters(options?.filters);
        setPreviewId(id);
      },
      closeDocument: () => setPreviewId(undefined),
      documentId: previewId,
    }),
    [previewId, setDocViewerFilters, setPreviewId],
  );

  return (
    <DocumentViewerContext.Provider value={context}>
      {children}
      {documentId && (
        <Suspense fallback={null}>
          <DocumentDrawer
            documentId={documentId}
            filters={filters ?? DEFAULT_DOCUMENT_FILTERS}
            open={isOpen}
            onOpenChange={onOpenChange}
            onPreviousDocument={() => goToSibling(index - 1)}
            onNextDocument={() => goToSibling(index + 1)}
            canGoToPreviousDocument={canGoToPreviousDocument}
            canGoToNextDocument={canGoToNextDocument}
          />
        </Suspense>
      )}
    </DocumentViewerContext.Provider>
  );
}

export function useDocumentViewer(): DocumentViewer {
  const context = useContext(DocumentViewerContext);
  if (context === undefined) {
    throw new Error("useDocumentViewer must be used within a DocumentViewerProvider");
  }
  return context;
}
